import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './ui/Modal';
import { GitMerge, Loader } from 'lucide-react';

/**
 * Resolve git sync conflicts per file: keep local (your version) or keep remote.
 *
 * @param {string[]} conflicts - relative file paths in conflict
 * @param {(resolutions:{path:string,side:'local'|'remote'}[])=>Promise} onResolve
 */
export default function GitConflictModal({
  isOpen,
  onClose,
  conflicts = [],
  onResolve,
  loading = false,
}) {
  const [choices, setChoices] = useState({});
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (!wasOpenRef.current) {
      // Default every file to "remote" (remote is treated as source of truth
      // unless the user explicitly keeps their local copy).
      const init = {};
      for (const p of conflicts) init[p] = 'remote';
      setChoices(init);
      wasOpenRef.current = true;
    }
  }, [isOpen, conflicts]);

  if (!isOpen) return null;

  const setAll = (side) => {
    const next = {};
    for (const p of conflicts) next[p] = side;
    setChoices(next);
  };

  const setOne = (path, side) => {
    setChoices((prev) => ({ ...prev, [path]: side }));
  };

  const handleResolve = async () => {
    const resolutions = conflicts.map((path) => ({
      path,
      side: choices[path] === 'local' ? 'local' : 'remote',
    }));
    await onResolve?.(resolutions);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Resolve sync conflicts"
      subtitle={`${conflicts.length} file${conflicts.length === 1 ? '' : 's'} differ between local and remote`}
      customIcon={<GitMerge size={22} />}
      preventBackdropClose={loading}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAll('local')}
            disabled={loading}
            className="settings-action-btn"
          >
            Keep all local
          </button>
          <button
            type="button"
            onClick={() => setAll('remote')}
            disabled={loading}
            className="settings-action-btn"
          >
            Keep all remote
          </button>
        </div>

        <div
          style={{
            maxHeight: '320px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {conflicts.map((path) => (
            <div
              key={path}
              className="settings-row"
              style={{ alignItems: 'center', gap: '12px' }}
            >
              <div className="min-w-0">
                <div
                  className="font-mono text-xs"
                  style={{ wordBreak: 'break-all' }}
                  title={path}
                >
                  {path}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setOne(path, 'local')}
                  disabled={loading}
                  className="settings-action-btn"
                  style={
                    choices[path] === 'local'
                      ? { background: 'var(--accent, #8869eb)', color: '#fff', borderColor: 'transparent' }
                      : undefined
                  }
                >
                  Local
                </button>
                <button
                  type="button"
                  onClick={() => setOne(path, 'remote')}
                  disabled={loading}
                  className="settings-action-btn"
                  style={
                    choices[path] !== 'local'
                      ? { background: 'var(--accent, #8869eb)', color: '#fff', borderColor: 'transparent' }
                      : undefined
                  }
                >
                  Remote
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={handleResolve}
          disabled={loading}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading && <Loader className="animate-spin" size={16} />}
          <span>{loading ? 'Resolving…' : 'Resolve & push'}</span>
        </button>
      </div>
    </Modal>
  );
}
